import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import type { Products } from '../../bff-api/products/model';
import type { ProductsAddedToBagDrawerContext } from './model/ProductsAddedToBagDrawerContext';

export const Context = createContext<ProductsAddedToBagDrawerContext>(
    {} as ProductsAddedToBagDrawerContext,
);

export const ProductsAddedToBagDrawerProvider: React.FC<PropsWithChildren> = ({
    children,
}: PropsWithChildren) => {
    const [productsAddedToBag, setProductsAddedToBag] = useState<Products>([]);

    const { isOpen, onOpen, onClose } = useDisclosure();

    const ctx = useMemo<ProductsAddedToBagDrawerContext>(
        (): ProductsAddedToBagDrawerContext =>
            ({
                isOpenDrawer: isOpen,
                onOpenDrawer: onOpen,
                onCloseDrawer: onClose,
                productsAddedToBag,
                setProductsAddedToBag: (
                    products: Products,
                    options?: { concatProducts?: boolean; maxProducts?: number },
                ): void => {
                    const { concatProducts = false, maxProducts = 6 } = options ?? {};
                    const newProductsAddedToBag = concatProducts
                        ? productsAddedToBag.concat(products)
                        : products;

                    setProductsAddedToBag(newProductsAddedToBag.slice(-maxProducts));
                },
            }) as ProductsAddedToBagDrawerContext,

        [productsAddedToBag, setProductsAddedToBag, isOpen, onOpen, onClose],
    );

    return <Context.Provider value={ctx}>{children}</Context.Provider>;
};

export const useProductsAddedToBagDrawer = (): ProductsAddedToBagDrawerContext =>
    useContext(Context);
